import { useState, useEffect, useCallback } from 'react';
import weighingService from '../services/weighing';

/**
 * Hook pour gérer l'autocomplétion avec stockage en base de données
 * @param key - Clé unique pour identifier le type de données (ex: 'provenance', 'destination')
 */
export const useAutocomplete = (key: 'provenance' | 'destination') => {
  const [values, setValues] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Charger les valeurs depuis l'API
  const loadValues = useCallback(async (): Promise<string[]> => {
    setLoading(true);
    try {
      const fetchedValues = await weighingService.getAutocompleteValues(key);
      setValues(fetchedValues);
      return fetchedValues;
    } catch (error) {
      console.error(`Erreur lors du chargement de ${key}:`, error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [key]);

  // Charger les valeurs au montage
  useEffect(() => {
    loadValues();
  }, [loadValues]);

  // Sauvegarder une nouvelle valeur (créer ou incrémenter)
  const saveValue = useCallback(async (value: string) => {
    if (!value || value.trim() === '') {
      return;
    }

    try {
      await weighingService.createOrIncrementAutocompleteValue(key, value);
      // Recharger les valeurs après sauvegarde
      await loadValues();
    } catch (error) {
      console.error(`Erreur lors de la sauvegarde de ${key}:`, error);
    }
  }, [key, loadValues]);

  // Obtenir les suggestions basées sur la recherche
  const getSuggestions = useCallback((searchTerm: string): string[] => {
    if (!searchTerm || searchTerm.trim() === '') {
      return values.slice(0, 10); // Retourner les 10 premières valeurs
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    
    return values.filter(value => 
      value.toLowerCase().includes(lowerSearchTerm)
    ).slice(0, 10);
  }, [values]);

  return {
    values,
    loading,
    loadValues,
    saveValue,
    getSuggestions
  };
};
